import React, { useEffect, useRef, useState } from 'react';

import Button from '@/components/global/Button';

import styles from './CodeBlock.module.scss';

interface Props {
  code: string;
  language: string;
  copyEnabled?: boolean;
  className?: string;
  copyButtonLabel?: string;
}

let didInit = false;

const CodeBlock: React.FC<Props> = ({
  className = '',
  code = '',
  language = '',
  copyEnabled = true,
  copyButtonLabel
}) => {
  const clipboardButtonStateToTextMap = {
    default: copyButtonLabel || 'Copy To Clipboard',
    success: 'Copied!',
    error: 'Error!'
  };

  const [clipboardSupported, setClipboardSupported] = useState<Boolean>(true);
  const [clipboardButtonState, setClipboardButtonState] =
    useState<keyof typeof clipboardButtonStateToTextMap>('default');

  const preEl = useRef<HTMLPreElement | null>(null);

  const copyContentToClipboard = async () => {
    setClipboardButtonState('default');

    try {
      const preCurrent = preEl?.current;
      const codeEl = preCurrent.querySelector('code');
      const code = codeEl.innerText;

      await navigator.clipboard.writeText(code);

      setClipboardButtonState('success');
    } catch (error) {
      setClipboardButtonState('error');
    }
  };

  useEffect(() => {
    if (clipboardButtonState === 'default') return;

    const timer = setTimeout(() => setClipboardButtonState('default'), 2000);

    return () => clearTimeout(timer);
  }, [clipboardButtonState]);

  useEffect(() => {
    if (!didInit) {
      didInit = true;
      // Check if clipboard API is supported before rendering copy button
      setClipboardSupported(
        window.navigator !== undefined && window.navigator.clipboard !== undefined
      );
    }
  }, []);

  return (
    <div className={[styles.CodeBlock, className].join(' ')}>
      <div className={styles.CodeBlock__inner}>
        <div className={styles.CodeBlock__label}>
          <span>{language}</span>
        </div>
        <div className={styles.CodeBlock__code}>
          <pre ref={preEl}>
            <code>{code}</code>
          </pre>
        </div>
        {clipboardSupported && copyEnabled && (
          <Button
            className={styles.CodeBlock__button}
            type="button"
            variant="secondary"
            callBack={copyContentToClipboard}
          >
            {clipboardButtonStateToTextMap[clipboardButtonState]}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CodeBlock;
